import SEO from '@/components/SEO';
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from '../../contexts/QuoteDrawerContext';
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from 'next-themes';
import { 
  Smartphone, Tablet, Download, Star, Award, Zap, Shield, Code, 
  ChevronDown, MessageSquare, Briefcase, HeartPulse, ShoppingCart, 
  Building2, ArrowRight, Check, SmartphoneCharging, Cpu, GitBranch, 
  Layers, Server, Code2, Smartphone as SmartphoneTouch, 
  Users, Clock, BarChart2, Globe, TabletSmartphone, SmartphoneNfc,
  Headphones, Wifi, Battery, Cpu as CpuIcon, HardDrive, Watch as Smartwatch, PowerOff as SmartphoneOff,
  User, Monitor, Cloud, PenTool
} from "lucide-react";

// Import app showcase icons
import HealthAppIcon from "@/components/app-showcase/HealthAppIcon";
import FoodAppIcon from "@/components/app-showcase/FoodAppIcon";
import FinanceAppIcon from "@/components/app-showcase/FinanceAppIcon";

// Import RelatedServices component
import { RelatedServices } from "@/components/RelatedServices";
import WebDesignLocations from '@/components/WebDesignLocations';

// Brand colors for app showcase
const brandColors = [
  '#6366F1', // Indigo
  '#10B981', // Emerald
  '#3B82F6', // Blue
  '#8B5CF6', // Violet
  '#EC4899', // Pink
  '#F59E0B', // Amber
  '#06B6D4'  // Cyan
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const FAQ = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-gray-200 pb-4">
      <button
        className="flex justify-between items-center w-full text-left py-3 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-lg">{question}</span>
        <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="mt-2 text-muted-foreground">
          {answer}
        </div>
      )}
    </div>
  );
};

function MobileAppDevelopment() {
  const { openDrawer } = useQuoteDrawer();
  const { setTheme } = useTheme();

  // Force light mode
  useEffect(() => {
    setTheme('light');
    document.documentElement.classList.remove('dark');
    document.documentElement.style.colorScheme = 'light';
  }, [setTheme]);

  const features = [
    {
      title: "Native iOS & Android",
      description: "High-performance apps built with Swift, Kotlin, and Java for optimal performance.",
      icon: <SmartphoneCharging className="h-8 w-8" />,
      color: "#3B82F6"
    },
    {
      title: "Cross-Platform",
      description: "Single codebase solutions using React Native and Flutter for cost efficiency.",
      icon: <Layers className="h-8 w-8" />,
      color: "#8B5CF6"
    },
    {
      title: "UI/UX Design",
      description: "Beautiful, intuitive interfaces that drive engagement and retention.",
      icon: <SmartphoneTouch className="h-8 w-8" />,
      color: "#EC4899"
    },
    {
      title: "Backend Development",
      description: "Scalable cloud infrastructure and APIs to power your mobile apps.",
      icon: <Server className="h-8 w-8" />,
      color: "#10B981"
    },
    {
      title: "App Store Optimization",
      description: "Maximize your app's visibility and conversion rates in app stores.",
      icon: <Download className="h-8 w-8" />,
      color: "#F59E0B"
    },
    {
      title: "Maintenance & Support",
      description: "Ongoing updates, bug fixes, and feature enhancements.",
      icon: <Code2 className="h-8 w-8" />,
      color: "#6366F1"
    }
  ];

  const technologies = [
    { name: "React Native", icon: <i className="fab fa-react text-4xl text-[#61DAFB]"></i>, color: "#61DAFB" },
    { name: "Flutter", icon: <i className="fas fa-mobile-alt text-4xl text-[#02569B]"></i>, color: "#02569B" },
    { name: "Swift", icon: <i className="fab fa-apple text-4xl text-gray-900"></i>, color: "#F05138" },
    { name: "Kotlin", icon: <i className="fab fa-android text-4xl text-[#3DDC84]"></i>, color: "#3DDC84" },
    { name: "Firebase", icon: <i className="fas fa-fire text-4xl text-[#FFCA28]"></i>, color: "#FFCA28" },
    { name: "AWS Amplify", icon: <i className="fab fa-aws text-4xl text-[#FF9900]"></i>, color: "#FF9900" },
    { name: "GraphQL", icon: <i className="fas fa-bolt text-4xl text-[#E10098]"></i>, color: "#E10098" },
    { name: "Redux", icon: <i className="fas fa-code-branch text-4xl text-[#764ABC]"></i>, color: "#764ABC" }
  ];

  const whyChooseUs = [
    {
      icon: <Award className="h-8 w-8" />,
      title: "Award-Winning Apps",
      description: "Recognized for excellence in mobile app development and design"
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "High Performance",
      description: "Optimized apps that run smoothly on all devices"
    },
    {
      icon: <Code className="h-8 w-8" />,
      title: "Clean Code",
      description: "Maintainable and scalable codebase following best practices"
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Security First",
      description: "Enterprise-grade security for your app and user data"
    }
  ];

  const industries = [
    {
      name: "Healthcare",
      icon: <HeartPulse className="h-6 w-6" />,
      description: "HIPAA compliant healthcare solutions"
    },
    {
      name: "E-commerce",
      icon: <ShoppingCart className="h-6 w-6" />,
      description: "Mobile shopping experiences"
    },
    {
      name: "Enterprise",
      icon: <Building2 className="h-6 w-6" />,
      description: "Business productivity apps"
    },
    {
      name: "Startups",
      icon: <Zap className="h-6 w-6" />,
      description: "MVP development & scaling"
    }
  ];

  const testimonials = [
    {
      name: "Dr. Sarah Al Mansoori",
      position: "Medical Director, HealthPlus Clinic",
      quote: "The app they developed for our clinic has transformed our patient management system. Highly recommended!",
      rating: 5
    },
    {
      name: "Khalid Al Farsi",
      position: "CEO, UrbanStyle",
      quote: "Our e-commerce app saw a 200% increase in mobile orders after the redesign. The team is exceptional!",
      rating: 5
    }
  ];

  const faqs = [
    {
      question: "How long does it take to develop a mobile app?",
      answer: "Most apps take 3-6 months from concept to launch, depending on complexity and features."
    },
    {
      question: "Do you provide app maintenance after launch?",
      answer: "Yes, we offer comprehensive maintenance and update packages to keep your app running smoothly."
    },
    {
      question: "What's the difference between native and cross-platform apps?",
      answer: "Native apps are built specifically for one platform (iOS or Android) while cross-platform apps work on both using a single codebase."
    }
  ];
  
  const [activeApp, setActiveApp] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout>();
  
  // App showcase data with proper typing
  interface AppShowcaseItem {
    name: string;
    category: string;
    description: string;
    features: string[];
    color: string;
    stats: { value: string; label: string }[];
    icon: React.ReactNode; // React component or element for the app icon
  }

  const appShowcase: AppShowcaseItem[] = [
    {
      name: "HealthTrack Pro",
      category: "Health & Wellness",
      description: "Comprehensive health monitoring with AI-powered insights and personalized recommendations.",
      features: ["Workout Plans", "Meal Tracking", "Health Analytics", "Doctor Connect"],
      color: brandColors[0],
      stats: [
        { value: "4.9", label: "App Store Rating" },
        { value: "500K+", label: "Downloads" },
        { value: "98%", label: "User Retention" }
      ],
      icon: <HealthAppIcon className="w-24 h-24 text-white" />
    },
    {
      name: "Foodie Express",
      category: "Food Delivery",
      description: "Fast food delivery from your favorite restaurants with real-time order tracking.",
      features: ["Live Order Tracking", "Instant Checkout", "Exclusive Deals", "Ratings & Reviews"],
      color: brandColors[2],
      stats: [
        { value: "4.8", label: "Customer Rating" },
        { value: "1M+", label: "Orders Delivered" },
        { value: "15min", label: "Avg. Delivery" }
      ],
      icon: <FoodAppIcon className="w-24 h-24 text-white" />
    },
    {
      name: "WealthGuard",
      category: "Fintech",
      description: "All-in-one financial management with investment tracking and bill payments.",
      features: ["Expense Analytics", "Investment Portfolio", "Bill Reminders", "Credit Score"],
      color: brandColors[4],
      stats: [
        { value: "4.7", label: "User Rating" },
        { value: "250K+", label: "Active Users" },
        { value: "$100M+", label: "Assets Managed" }
      ],
      icon: <FinanceAppIcon className="w-24 h-24 text-white" />
    }
  ];

  // Auto-rotate app showcase
  useEffect(() => {
    if (!isHovered) {
      intervalRef.current = setInterval(() => {
        setActiveApp((prev) => (prev + 1) % appShowcase.length);
      }, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isHovered, appShowcase.length]);

  const TestimonialCard = ({ testimonial }: { testimonial: { 
    name: string;
    position: string;
    quote: string;
    rating: number;
  } }) => (
    <div className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all">
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-4">
          <User className="h-6 w-6" />
        </div>
        <div>
          <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
          <p className="text-sm text-gray-500">{testimonial.position}</p>
        </div>
      </div>
      <p className="text-gray-600 mb-4">"{testimonial.quote}"</p>
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`h-5 w-5 ${i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
          />
        ))}
      </div>
    </div>
  );

  return (
    <>
      <SEO 
        title="Mobile App Development UAE | First Soft UAE"
        description="Professional iOS & Android app development in UAE. Custom mobile solutions for businesses since 2017 with 100+ apps delivered."
        keywords="mobile app development uae, ios android dubai, custom apps sharjah"
      />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-50 py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('/svgs/pattern-grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-blue-50/80"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-gray-200 shadow-sm mb-6"
              >
                <span className="relative flex h-3 w-3 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                </span>
                <span className="text-sm font-medium text-gray-700">Transforming Ideas Into Mobile Experiences</span>
              </motion.div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Mobile Apps That <span className="text-blue-600">Drive Results</span>
              </h1>
              
              <p className="text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 mb-8">
                We create beautiful, high-performance mobile applications that captivate users and deliver measurable business impact.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  onClick={openDrawer}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg transition-all hover:shadow-lg"
                >
                  Get Free Consultation
                </Button>
                <Link href="/portfolio">
                  <Button 
                    variant="outline"
                    className="border-blue-200 bg-white text-blue-700 hover:bg-blue-50 px-8 py-3 text-lg font-medium rounded-lg transition-all"
                  >
                    View Our Work
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Phone Mockup */}
            <motion.div 
              className="relative hidden lg:block"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="relative mx-auto w-72 h-[580px] bg-white rounded-[40px] border-[14px] border-gray-100 overflow-hidden shadow-2xl">
                {/* Phone screen content */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 p-6">
                  <div className="flex justify-between items-center mb-8">
                    <div className="text-gray-900 font-bold text-xl">Zell</div>
                    <div className="text-xs text-gray-500">9:41 AM</div>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                      <div className="h-3 bg-gray-200 rounded-full w-3/4 mb-2"></div>
                      <div className="h-2 bg-gray-100 rounded-full w-1/2"></div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-2xl text-white shadow-lg">
                      <h3 className="font-bold text-lg mb-2">Your App Here</h3>
                      <p className="text-sm opacity-90 mb-4">Let's build something amazing together</p>
                      <button className="bg-white text-blue-700 text-sm font-medium px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors">Get Started</button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="aspect-square bg-white rounded-xl shadow-sm border border-gray-100"></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* App Showcase Section */}
      <section id="portfolio" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Link href="/portfolio" className="group inline-block">
              <div className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-700 mb-4 group-hover:bg-blue-200/80 transition-colors">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                Our Work
                <ArrowRight className="ml-2 h-4 w-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all text-blue-600" />
              </div>
            </Link>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Featured Mobile Apps</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explore our portfolio of successful mobile applications that deliver exceptional user experiences and business results.
            </p>
          </motion.div>

          <div className="relative max-w-6xl mx-auto">
            {/* App Navigation Dots */}
            <div className="flex justify-center space-x-2 mb-8">
              {appShowcase.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveApp(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${index === activeApp ? 'bg-blue-400 w-8' : 'bg-white/20'}`}
                  aria-label={`View ${appShowcase[index].name}`}
                />
              ))}
            </div>

            {/* App Showcase */}
            <div 
              className="relative h-[600px] md:h-[700px] rounded-3xl overflow-hidden"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              key={`app-showcase-${activeApp}`}
            >
              <AnimatePresence mode="wait">
                {appShowcase.map((app, index) => (
                  index === activeApp && (
                    <motion.div
                      key={app.name}
                      initial={{ opacity: 0, x: 100 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0 flex flex-col md:flex-row"
                    >
                      {/* App Mockup */}
                      <div className="relative flex-1 flex items-center justify-center p-8 md:p-12 bg-gradient-to-br from-blue-50 to-indigo-50">
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center"></div>
                        </div>
                        <div className="relative w-full max-w-xs">
                          <div className="relative mx-auto w-full h-[600px] bg-white rounded-[40px] border-[14px] border-gray-100 overflow-hidden shadow-2xl">
                            <div className="absolute inset-0" style={{ backgroundColor: app.color }}>
                              <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-white/50 p-6 flex flex-col">
                                <div className="flex justify-between items-center mb-8">
                                  <div className="flex items-center">
                                    <div className="mr-3 bg-white/80 p-1.5 rounded-lg">
                                      {app.icon}
                                    </div>
                                    <div className="text-gray-900 font-bold text-xl">{app.name.split(' ')[0]}</div>
                                  </div>
                                  <div className="text-xs text-gray-700 bg-white/80 px-2 py-1 rounded-full">9:41 AM</div>
                                </div>
                                <div className="flex-1 flex flex-col justify-center">
                                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{app.name}</h3>
                                  <p className="text-gray-800 text-sm mb-6">{app.description}</p>
                                  <div className="grid grid-cols-3 gap-3 mb-6">
                                    {app.stats.map((stat, i) => (
                                      <div key={i} className="bg-white/80 backdrop-blur-sm p-3 rounded-xl text-center shadow-sm">
                                        <div className="text-xl font-bold text-gray-900">{stat.value}</div>
                                        <div className="text-xs text-gray-600">{stat.label}</div>
                                      </div>
                                    ))}
                                  </div>
                                  <button className="bg-white text-gray-900 font-medium py-2 px-6 rounded-full self-start hover:bg-gray-50 transition-colors shadow-sm">
                                    Learn More
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* App Details */}
                      <div className="flex-1 bg-white p-8 md:p-12 flex flex-col justify-center border-l border-gray-100">
                        <div className="max-w-md mx-auto w-full">
                          <span className="inline-block px-3 py-1 text-xs font-medium rounded-full mb-4 bg-blue-100 text-blue-700">
                            {app.category}
                          </span>
                          <h3 className="text-3xl font-bold text-gray-900 mb-4">{app.name}</h3>
                          <p className="text-gray-600 mb-6">{app.description}</p>
                          
                          <div className="space-y-3 mb-8">
                            <h4 className="font-medium text-gray-900">Key Features:</h4>
                            <ul className="space-y-2">
                              {app.features.map((feature, i) => (
                                <li key={i} className="flex items-start">
                                  <Check className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-700">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className="flex flex-wrap gap-3">
                            <Link href="/portfolio">
                              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                                View Case Study
                              </Button>
                            </Link>
                            <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-100">
                              Live Demo
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* App Showcase Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">App Showcase</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Examples of mobile apps we've developed across various industries
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {appShowcase.map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-gray-100"
              >
                <div 
                  className="h-48 w-full" 
                  style={{ backgroundColor: `${app.color}20` }}
                >
                  {/* App preview placeholder */}
                </div>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mr-4" style={{ backgroundColor: app.color, color: 'white' }}>
                      {app.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{app.name}</h3>
                      <p className="text-sm text-gray-500">{app.category}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">{app.description}</p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {app.features.map((feature, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {feature}
                      </span>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full border-gray-300 text-gray-700 hover:bg-gray-100"
                  >
                    View Case Study
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mobile App Development Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive solutions from concept to launch and beyond
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all"
              >
                <div 
                  className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: `${feature.color}20`, color: feature.color }}
                >
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* App Development Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">App Development Process</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From idea to app store - our proven development methodology
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Concept & Strategy", description: "Define app requirements and create development roadmap" },
              { step: "02", title: "UI/UX Design", description: "Create intuitive and engaging user interfaces" },
              { step: "03", title: "Development & Testing", description: "Build and rigorously test your mobile application" },
              { step: "04", title: "App Store Submission", description: "Deploy and optimize for app store success" }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="font-orbitron font-bold text-white">{item.step}</span>
                </div>
                <h3 className="font-orbitron font-semibold text-lg mb-2 text-gray-900">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Why Choose Our Mobile Solutions?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We create mobile experiences that users love and businesses trust
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl text-center shadow-sm hover:shadow-md transition-all border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white">
                  {React.cloneElement(item.icon, { className: 'h-6 w-6' })}
                </div>
                <h3 className="font-orbitron font-semibold text-xl mb-4 text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* App Showcase */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Our App Showcase</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explore some of our successful mobile applications
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {appShowcase.map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden group hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="h-48 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-center p-4">
                  <div className="h-32 w-32 text-blue-600">
                    {app.icon}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-orbitron font-semibold text-xl text-gray-900">{app.name}</h3>
                    <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full font-medium">
                      {app.category}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {app.features.map((feature, i) => (
                      <span key={i} className="px-2 py-1 bg-gray-50 text-gray-700 text-xs rounded border border-gray-100">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Industries We Serve</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Custom mobile solutions for diverse business needs
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl hover:shadow-md transition-all border border-gray-100"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                  {React.cloneElement(industry.icon, { className: 'h-6 w-6' })}
                </div>
                <h3 className="font-orbitron font-semibold text-lg mb-2 text-gray-900">{industry.name}</h3>
                <p className="text-gray-600 text-sm">{industry.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Client Success Stories</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-gray-50 p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-4">
                    <User className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500">{testimonial.position}</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-4">"{testimonial.quote}"</p>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`h-5 w-5 ${i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about our mobile app development services
            </p>
          </motion.div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQ key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Mobile Technologies We Use</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Cutting-edge frameworks for powerful mobile applications
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {technologies.map((tech, index) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl text-center hover:shadow-md transition-all border border-gray-100"
              >
                <i className={`${tech.icon} text-4xl text-blue-600 mb-4`}></i>
                <p className="font-orbitron text-sm text-gray-700">{tech.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Client Success Stories</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-4">
                    <User className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500">{testimonial.position}</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-4">"{testimonial.quote}"</p>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`h-5 w-5 ${i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <RelatedServices 
            currentService="Mobile App Development" 
            services={[
              {
                title: "Web Design & Development",
                description: "Custom websites and web applications tailored to your business needs.",
                icon: <Monitor className="h-8 w-8" />
              },
              {
                title: "Cloud Services",
                description: "Scalable, secure, and high-performance cloud solutions tailored to your business needs.",
                icon: <Cloud className="h-8 w-8" />
              },
              {
                title: "UI/UX Design",
                description: "User-centered design solutions that enhance user experience and engagement.",
                icon: <PenTool className="h-8 w-8" />
              }
            ]} 
          />
        </div>
      </section>

      <WebDesignLocations />

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to build your mobile app?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Let's discuss how we can turn your idea into a successful mobile application
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={openDrawer}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg transition-all hover:shadow-lg"
              >
                Get Started
              </Button>
              <Button 
                variant="outline"
                className="border-blue-200 bg-white text-blue-700 hover:bg-blue-50 px-8 py-3 text-lg font-medium rounded-lg transition-all"
              >
                Call Us: +971-58-248-1443
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}

export default MobileAppDevelopment;
